import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, CheckCircle2, ArrowRight, ChevronDown, Coins, Layers } from 'lucide-react';
import CryptoAffiliateBlock from '../../../components/CryptoAffiliateBlock';
import RiskAnalysisBlock from '../../../components/RiskAnalysisBlock';

const CardanoProfitCalculatorTool = () => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [investment, setInvestment] = useState('');
  const [fees, setFees] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');
  const [showCoinMenu, setShowCoinMenu] = useState(false);

  useEffect(() => {
    calculate();
  }, [buyPrice, sellPrice, investment, fees]);

  const calculate = () => {
    const buy = parseFloat(buyPrice);
    const sell = parseFloat(sellPrice);
    const invest = parseFloat(investment);
    const fee = parseFloat(fees) || 0;

    if (isNaN(buy) || isNaN(sell) || isNaN(invest) || buy <= 0 || invest <= 0) {
      setResult(null);
      return;
    }

    const adaPurchased = invest / buy;
    const revenue = adaPurchased * sell;
    const grossProfit = revenue - invest;
    const netProfit = grossProfit - fee;
    const roi = (netProfit / invest) * 100;
    const breakEven = (invest + fee) / adaPurchased;

    setResult({
      adaPurchased,
      revenue,
      grossProfit,
      netProfit,
      roi,
      breakEven,
      isProfitable: netProfit > 0,
      isBreakEven: Math.abs(netProfit) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBuyPrice('');
    setSellPrice('');
    setInvestment('');
    setFees('');
    setResult(null);
  };

  const formatCurrency = (value) => '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (value) => (value >= 0 ? '+' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

  const scenarios = [
    { label: 'ADA: $0.30 → $0.80', buy: '0.30', sell: '0.80', invest: '1000' },
    { label: 'ADA: $0.50 → $1.50', buy: '0.50', sell: '1.50', invest: '2000' },
    { label: 'ADA: $0.40 → $2.00', buy: '0.40', sell: '2.00', invest: '3000' },
  ];

  const applyScenario = (scenario) => {
    setBuyPrice(scenario.buy);
    setSellPrice(scenario.sell);
    setInvestment(scenario.invest);
    setFees('5');
  };

  const coinOptions = [
    { name: 'Cardano (ADA)', slug: 'cardano-profit-calculator', icon: '₳', current: true },
    { name: 'Bitcoin (BTC)', slug: 'bitcoin-profit-calculator', icon: '₿', current: false },
    { name: 'Ethereum (ETH)', slug: 'ethereum-profit-calculator', icon: 'Ξ', current: false },
    { name: 'Solana (SOL)', slug: 'solana-profit-calculator', icon: '◎', current: false },
    { name: 'Polygon (MATIC)', slug: 'polygon-profit-calculator', icon: '⬡', current: false },
    { name: 'All Crypto', slug: 'crypto-profit-calculator', icon: '🪙', current: false },
  ];

  return (
    <div className="space-y-8" data-testid="cardano-profit-calculator">
      {/* Coin Selector */}
      <div className="relative">
        <button
          onClick={() => setShowCoinMenu(!showCoinMenu)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-500/30 transition-colors"
          data-testid="coin-selector"
        >
          <span className="text-xl font-bold">₳</span>
          <span className="font-medium">Cardano Profit Calculator</span>
          <ChevronDown size={16} className={`transition-transform ${showCoinMenu ? 'rotate-180' : ''}`} />
        </button>
        
        {showCoinMenu && (
          <div className="absolute top-full left-0 mt-2 w-72 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
            {coinOptions.map((coin) => (
              coin.current ? (
                <div key={coin.slug} className="flex items-center gap-3 px-4 py-3 bg-blue-500/10 text-blue-400 cursor-default">
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <CheckCircle2 size={16} className="ml-auto" />
                </div>
              ) : (
                <Link
                  key={coin.slug}
                  to={`/tools/${coin.slug}`}
                  className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 transition-colors"
                  onClick={() => setShowCoinMenu(false)}
                >
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <ArrowRight size={16} className="ml-auto opacity-50" />
                </Link>
              )
            ))}
          </div>
        )}
      </div>

      {/* Quick Examples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Calculator */}
      <div className="p-6 bg-gray-800 rounded-xl space-y-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
            <span className="text-2xl font-bold text-blue-400">₳</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Cardano Profit Calculator</h3>
            <p className="text-sm text-gray-400">Calculate your ADA investment profit — built for sustainability</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Buy Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.001"
                min="0"
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                placeholder="e.g., 0.40"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-lg"
                data-testid="buy-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sell Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.001"
                min="0"
                value={sellPrice}
                onChange={(e) => setSellPrice(e.target.value)}
                placeholder="e.g., 1.00"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-lg"
                data-testid="sell-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Investment Amount (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={investment}
                onChange={(e) => setInvestment(e.target.value)}
                placeholder="e.g., 1000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-lg"
                data-testid="investment-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Trading Fees (USD) <span className="text-gray-500">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={fees}
                onChange={(e) => setFees(e.target.value)}
                placeholder="e.g., 5"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">ADA has low fees (~$0.20-0.50 per transaction)</p>
          </div>
        </div>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors text-sm"
        >
          <Trash2 size={14} />
          Clear All
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          <div className={`p-6 rounded-xl border-2 ${result.isProfitable ? 'bg-green-500/10 border-green-500/40' : result.isBreakEven ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {result.isProfitable ? <TrendingUp className="text-green-400" size={28} /> : <TrendingDown className="text-red-400" size={28} />}
                <span className="text-lg font-medium text-gray-200">
                  {result.isProfitable ? 'Profit' : result.isBreakEven ? 'Break Even' : 'Loss'}
                </span>
              </div>
              <button onClick={() => copyResult(result.netProfit, 'profit')} className="p-2 hover:bg-gray-700 rounded-lg">
                {copied === 'profit' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <div className="text-center">
              <p className={`text-5xl font-bold ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {result.netProfit >= 0 ? '+' : ''}{formatCurrency(result.netProfit)}
              </p>
              <p className={`text-2xl mt-2 ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatPercent(result.roi)} ROI
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">ADA Purchased</p>
              <p className="text-xl font-semibold text-blue-400">{result.adaPurchased.toFixed(2)} ADA</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Sale Value</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(result.revenue)}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Gross Profit</p>
              <p className={`text-xl font-semibold ${result.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {result.grossProfit >= 0 ? '+' : ''}{formatCurrency(result.grossProfit)}
              </p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Break-even Price</p>
              <p className="text-xl font-semibold text-orange-400">{formatCurrency(result.breakEven)}</p>
            </div>
          </div>

          <CryptoAffiliateBlock 
            profit={result.netProfit} 
            cryptoName="ADA"
            toolName="Cardano Profit Calculator"
            toolSlug="cardano-profit-calculator"
          />
        </div>
      )}

      {/* Related Calculators */}
      <div className="p-5 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Coins size={20} className="text-blue-400" />
          Related Crypto Calculators
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link to="/tools/crypto-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-green-400">Crypto Profit</p>
              <p className="text-sm text-gray-400">All coins</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-green-400" />
          </Link>
          <Link to="/tools/crypto-roi-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-blue-400">Crypto ROI</p>
              <p className="text-sm text-gray-400">Return analysis</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-blue-400" />
          </Link>
          <Link to="/tools/crypto-tax-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-purple-400">Crypto Tax</p>
              <p className="text-sm text-gray-400">Tax estimates</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-purple-400" />
          </Link>
          <Link to="/tools/ethereum-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-indigo-400">Ethereum Profit</p>
              <p className="text-sm text-gray-400">ETH calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-indigo-400" />
          </Link>
        </div>
      </div>

      {/* SEO Content */}
      <div className="space-y-8 pt-6 border-t border-gray-700">
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">What is a Cardano Profit Calculator?</h2>
          <div className="prose prose-invert max-w-none text-gray-300 space-y-4">
            <p>
              A <strong>Cardano Profit Calculator</strong> is an essential tool for ADA investors to calculate 
              profits from trading Cardano, one of the most scientifically rigorous blockchain projects. Founded 
              by Ethereum co-founder Charles Hoskinson, Cardano takes a peer-reviewed, research-first approach to 
              blockchain development. This "slow and steady" philosophy has built a loyal community of investors 
              who believe in long-term, sustainable growth over hype-driven pumps.
            </p>
            <p>
              Our <strong>ADA profit calculator</strong> helps you determine your profit or loss by entering your 
              buy price, sell price, and investment amount. Cardano's Ouroboros proof-of-stake consensus mechanism 
              means holders can earn passive income through staking (typically 4-5% APY), which compounds your 
              investment returns beyond just price appreciation. The calculator shows your total ADA acquired, 
              sale value, net profit, and ROI instantly.
            </p>
            <p>
              Whether you're a long-term holder staking for passive income, a trader capitalizing on price movements, 
              or someone evaluating Cardano's potential with smart contracts and DeFi, this calculator provides 
              accurate profit projections. With its focus on sustainability, scalability, and interoperability, 
              Cardano represents a different investment thesis than more volatile meme coins — and tracking your 
              ADA profits accurately reflects that measured approach.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">How to Use the Cardano Profit Calculator</h2>
          <div className="space-y-3">
            {[
              { step: 1, title: 'Enter Your ADA Buy Price', desc: 'Input the price per ADA when you purchased. Cardano typically trades between $0.20-$3.00, depending on market conditions.' },
              { step: 2, title: 'Enter Your ADA Sell Price', desc: 'Input the price at which you sold (or plan to sell). Check real-time ADA prices on exchanges or CoinGecko.' },
              { step: 3, title: 'Enter Your Investment Amount', desc: 'The total USD invested. The calculator shows how many ADA coins you acquired at your entry price.' },
              { step: 4, title: 'Add Trading Fees (Optional)', desc: 'Include exchange fees. Cardano network fees are low (~$0.20-0.50), but exchange fees vary (0.1%-0.5%).' },
              { step: 5, title: 'View Your Results', desc: 'Instantly see your profit/loss, ROI percentage, ADA quantity, and break-even price to inform your trading decisions.' },
            ].map((item) => (
              <div key={item.step} className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold flex-shrink-0">{item.step}</div>
                <div>
                  <h3 className="font-semibold text-white">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Example Cardano Profit Calculation</h2>
          <div className="p-5 bg-gray-800 rounded-lg border border-gray-700">
            <h3 className="font-semibold text-white mb-3">Scenario: ADA Smart Contract Rally</h3>
            <div className="space-y-3 text-gray-300">
              <p>Let's calculate profit from a Cardano trade during a development milestone:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Buy Price:</strong> $0.35 per ADA</li>
                <li><strong>Sell Price:</strong> $1.20 per ADA</li>
                <li><strong>Investment:</strong> $1,750</li>
                <li><strong>Trading Fees:</strong> $8.75 (0.5% total)</li>
              </ul>
              <div className="mt-4 p-4 bg-gray-900 rounded-lg font-mono text-sm">
                <p><span className="text-gray-400">ADA Purchased:</span> <span className="text-blue-400">$1,750 / $0.35 = 5,000 ADA</span></p>
                <p><span className="text-gray-400">Sale Value:</span> <span className="text-white">5,000 ADA × $1.20 = $6,000</span></p>
                <p><span className="text-gray-400">Gross Profit:</span> <span className="text-green-400">$6,000 - $1,750 = $4,250</span></p>
                <p><span className="text-gray-400">Net Profit:</span> <span className="text-green-400">$4,250 - $8.75 = $4,241.25</span></p>
                <p><span className="text-gray-400">ROI:</span> <span className="text-blue-400">($4,241.25 / $1,750) × 100 = 242.4%</span></p>
              </div>
              <p className="text-sm text-gray-400 mt-3">
                This trade turned $1,750 into $5,991.25 after fees — a 242.4% return. Note: Staking rewards would 
                add additional gains on top of price appreciation, potentially 4-5% more ADA per year.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'What makes Cardano different from other cryptocurrencies?', a: 'Cardano uses a peer-reviewed, research-first approach. Every protocol upgrade is academically reviewed before implementation. It uses Ouroboros proof-of-stake (more energy-efficient than Bitcoin) and focuses on scalability through layers (settlement + computation).' },
              { q: 'Can I earn passive income with Cardano?', a: 'Yes! ADA holders can stake their coins to earn approximately 4-5% APY in staking rewards. Unlike Ethereum, Cardano staking doesn\'t lock your funds — you can unstake anytime. This passive income adds to your profit beyond price appreciation.' },
              { q: 'What was Cardano\'s all-time high?', a: 'Cardano reached approximately $3.10 in September 2021 during the Alonzo smart contracts launch. Many investors calculate potential profits based on ADA returning to or exceeding this level.' },
              { q: 'Is Cardano a good long-term investment?', a: 'Cardano appeals to investors who value sustainability, research-backed development, and proof-of-stake efficiency. However, it faces competition from Ethereum, Solana, and others. As with all crypto, only invest what you can afford to lose.' },
              { q: 'How do Cardano transaction fees compare?', a: 'Cardano fees are typically $0.20-0.50 per transaction — lower than Ethereum but higher than Solana. Fees are designed to prevent spam while remaining affordable for users and DeFi applications.' },
            ].map((faq, i) => (
              <div key={i} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
        <h4 className="text-sm font-medium text-gray-200 mb-2 flex items-center gap-2">
          <Layers size={16} className="text-blue-400" />
          Cardano's Layered Architecture
        </h4>
        <p className="text-sm text-gray-400">
          Cardano separates settlement (transfers) from computation (smart contracts) into two layers. This design 
          enables upgrades without hard forks and improves scalability. When calculating profits, consider that 
          ADA's value proposition extends beyond speculation to infrastructure for decentralized applications.
        </p>
      </div>
    </div>
  );
};

export default CardanoProfitCalculatorTool;
